import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Share2, Trash2, Plus, Flower2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startLogin } from "@/components/Navbar";

export default function MyBouquets() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bouquets, setBouquets] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { setBouquets([]); return; }
    api.get("/bouquets").then((r) => setBouquets(r.data)).catch(() => setBouquets([]));
  }, [user, loading]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/bouquets/${id}`);
      setBouquets((prev) => prev.filter((b) => b.id !== id));
      toast.success("Bouquet removed.");
    } catch { toast.error("Could not delete."); }
  };

  const handleShare = async (shareId) => {
    const url = `${window.location.origin}/b/${shareId}`;
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied!");
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h1 className="font-serif-display text-4xl sm:text-5xl">My Bouquets</h1>
            <p className="text-muted-foreground mt-2">Your saved wedding arrangements.</p>
          </div>
          <Link to="/studio" data-testid="new-bouquet-btn" className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">
            <Plus className="w-4 h-4" /> New bouquet
          </Link>
        </div>

        {!user && !loading ? (
          <div className="text-center py-24">
            <Flower2 className="w-10 h-10 text-primary mx-auto mb-4" />
            <p className="text-lg mb-6">Sign in to view and save your bouquets.</p>
            <button onClick={startLogin} data-testid="signin-prompt-btn" className="rounded-full px-7 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">
              Sign in with Google
            </button>
          </div>
        ) : bouquets === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : bouquets.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-lg text-muted-foreground mb-6">No bouquets yet. Let's design your first one.</p>
            <button onClick={() => navigate("/studio")} data-testid="empty-start-btn" className="rounded-full px-7 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">
              Open the studio
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bouquets-grid">
            {bouquets.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-border overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-300"
                data-testid={`bouquet-card-${b.id}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-serif-display text-2xl leading-tight">{b.name}</h3>
                    <span className="font-serif-display text-xl text-primary shrink-0">${b.price.total.toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => handleShare(b.share_id)} data-testid={`share-${b.id}`} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium border border-primary text-primary hover:bg-primary/5 transition-all">
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                    <button onClick={() => handleDelete(b.id)} data-testid={`delete-${b.id}`} className="inline-flex items-center justify-center rounded-full w-10 h-10 border border-border hover:border-destructive hover:text-destructive transition-all">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
